/**
 *
 * A wrapper around GPURenderPassEncoder or GPURenderBundleEncoder that adds
 * state-tracking methods to eliminate redundant GPU state changes.
 *
 * The original encoder methods are proxied and overridden where necessary.
 * Use ".untracked" or ".encoder" to access the original encoder directly.
 *
 */
export class StateTrackingEncoder {
  /**
   * The underlying encoder being wrapped
   * @type {GPURenderPassEncoder|GPURenderBundleEncoder}
   */
  encoder = null;

  /**
   * Alias for this.encoder. Syntactic sugar to write `passEncoder.untracked.setBindGroup(..)`
   * @type {GPURenderPassEncoder|GPURenderBundleEncoder}
   */
  untracked = null;

  /**
   * True if this wraps a GPURenderBundleEncoder, false for GPURenderPassEncoder
   * @type {boolean}
   */
  isBundleEncoder = false;

  // State tracking
  #activePipeline = null;
  #activeBindGroups = new Map(); // slot -> bindGroup

  /**
   * @param {GPURenderPassEncoder|GPURenderBundleEncoder} encoder
   */
  constructor(encoder) {
    this.encoder = encoder;
    this.untracked = this.encoder; // alias

    // Detect if this is a bundle encoder (GPURenderBundleEncoder has finish(), GPURenderPassEncoder has end()).
    this.isBundleEncoder = typeof encoder.finish === 'function';

    // Create forwarding methods/getters/setters to the underlying encoder.
    // Locally defined methods/properties take precedence and are not forwarded.
    for (const prop in encoder) {
      if (prop in this) {
        continue;
      }

      const value = encoder[prop];
      if (typeof value === 'function') {
        // Forward method properties to the encoder
        this[prop] = value.bind(encoder);
      } else {
        // Create forwarding getter/setter for data properties.
        Object.defineProperty(this, prop, {
          get() {
            return encoder[prop];
          },
          set(newValue) {
            encoder[prop] = newValue;
          },
          enumerable: true,
          configurable: true
        });
      }
    }
  }

  // ===================================================================
  // Overridden methods
  // ===================================================================

  /**
   * Executes a bundle and resets tracked state as per spec.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/GPURenderPassEncoder/executeBundles
   */
  executeBundles(bundles) {
    this.encoder.executeBundles(bundles);
    this.#resetForExecuteBundles();
  }

  setPipeline() {
    console.error("Call setPipelineIfNeeded instead. If you want to bypass state tracking call via .untracked.<method>");
  }

  setBindGroup() {
    console.error("Call setBindGroupIfNeeded instead. If you want to bypass state tracking call via .untracked.<method>");
  }

  // ===================================================================
  // Added methods
  // ===================================================================

  /**
   * "After calling executeBundles() the currently set vertex buffers, index buffers,
   * bind groups, and pipeline are all cleared, even if no bundles are actually executed."
   * @see https://developer.mozilla.org/en-US/docs/Web/API/GPURenderPassEncoder/executeBundles
   */
  #resetForExecuteBundles() {
    this.#activePipeline = null;
    this.#activeBindGroups.clear();
    // // If these are added, they need to be reset as well.
    // this.#indexState = null;
    // this.#vertexState = null;
  }

  /**
   * Set pipeline only if it differs from the currently active pipeline
   * @param {GPURenderPipeline} pipeline
   */
  setPipelineIfNeeded(pipeline) {
    if (this.#activePipeline !== pipeline) {
      this.encoder.setPipeline(pipeline);
      this.#activePipeline = pipeline;
    }
  }

  /**
   * Set bind group only if it differs from the currently active bind group at this slot
   * @param {number} index
   * @param {GPUBindGroup} bindGroup
   */
  setBindGroupIfNeeded(index, bindGroup) {
    if (arguments.length > 2) {
      console.error("dynamicOffsets are not implemented.");
      return;
    }

    const current = this.#activeBindGroups.get(index);
    if (current !== bindGroup) {
      this.encoder.setBindGroup(index, bindGroup);
      this.#activeBindGroups.set(index, bindGroup);
    }
  }
}